/* Prayer statistics — pure functions over the prayer log (unit-tested in
   tests/unit/prayer-stats.test.ts). Periods are calendar periods (Mon–Sun
   week, month, year) and never count days after `today`. */
import { SALAH, addDays } from '@shared/prayer';
import type { PrayerDay, PrayerStatus } from '@/lib/types';
import { RAWATIB, SUNNAH, type SunnahKey } from './sunnah';

export type Period = 'week' | 'month' | 'year';
type Log = Record<string, PrayerDay>;

/* Four display groups — "late" and "made up" share one, so the chart palette
   stays at four distinguishable colours (validated for colour-blindness). */
export type StatGroup = 'jamaah' | 'ontime' | 'late' | 'missed';
export const GROUP_OF: Record<PrayerStatus, StatGroup> = { jamaah: 'jamaah', ontime: 'ontime', late: 'late', qada: 'late', missed: 'missed' };
export const GROUPS: { key: StatGroup; label: string }[] = [
  { key: 'jamaah', label: 'In jamāʿah' },
  { key: 'ontime', label: 'On time' },
  { key: 'late', label: 'Late / made up' },
  { key: 'missed', label: 'Missed' },
];

const PRAYED: PrayerStatus[] = ['jamaah', 'ontime', 'late', 'qada'];
const ON_TIME: PrayerStatus[] = ['jamaah', 'ontime'];

const pad = (n: number) => String(n).padStart(2, '0');
const parse = (k: string) => { const [y, m, d] = k.split('-').map(Number); return { y, m, d }; };

export function periodRange(period: Period, ref: string): { start: string; end: string } {
  const { y, m } = parse(ref);
  if (period === 'week') {
    const dow = (new Date(`${ref}T12:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
    const start = addDays(ref, -dow);
    return { start, end: addDays(start, 6) };
  }
  if (period === 'month') {
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(last)}` };
  }
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

export function shiftPeriod(period: Period, ref: string, delta: number): string {
  const { y, m, d } = parse(ref);
  if (period === 'week') return addDays(ref, 7 * delta);
  if (period === 'month') { const t = new Date(Date.UTC(y, m - 1 + delta, 1)); return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-01`; }
  return `${y + delta}-${pad(m)}-${pad(Math.min(d, 28))}`;
}

export function periodLabel(period: Period, ref: string): string {
  const { start, end } = periodRange(period, ref);
  const fmt = (k: string, o: Intl.DateTimeFormatOptions) => new Date(`${k}T12:00:00Z`).toLocaleDateString('en-GB', { ...o, timeZone: 'UTC' });
  if (period === 'week') return `${fmt(start, { day: 'numeric', month: 'short' })} – ${fmt(end, { day: 'numeric', month: 'short' })}`;
  if (period === 'month') return fmt(start, { month: 'long', year: 'numeric' });
  return String(parse(start).y);
}

/** Every day of the period up to and including today. */
export function daysIn(period: Period, ref: string, today: string): string[] {
  const { start, end } = periodRange(period, ref);
  const out: string[] = [];
  for (let d = start; d <= end && d <= today; d = addDays(d, 1)) out.push(d);
  return out;
}

export interface PeriodStats {
  days: number;
  possible: number;
  prayed: number;
  onTime: number;
  jamaah: number;
  missed: number;
  logged: number;
  perPrayer: Record<(typeof SALAH)[number], Record<StatGroup | 'none', number>>;
  sunnah: Record<SunnahKey, { done: number; rakats: number }>;
  rawatibDays: number;
  /** days excused by a period — left out of `possible` */
  excusedDays: number;
}

export function periodStats(log: Log, days: string[], excused: (d: string) => boolean = () => false): PeriodStats {
  const perPrayer = Object.fromEntries(SALAH.map((p) => [p, { jamaah: 0, ontime: 0, late: 0, missed: 0, none: 0 }])) as PeriodStats['perPrayer'];
  const sunnah = Object.fromEntries(SUNNAH.map((s) => [s.key, { done: 0, rakats: 0 }])) as PeriodStats['sunnah'];
  let prayed = 0, onTime = 0, jamaah = 0, missed = 0, logged = 0, rawatibDays = 0, excusedDays = 0, possible = 0;
  for (const d of days) {
    const day = log[d];
    const ex = excused(d);
    if (ex) excusedDays++;
    for (const p of SALAH) {
      const st = day?.marks[p];
      // On an excused day only what was actually logged counts (e.g. ʿIshāʾ after ghusl).
      if (!st) { if (!ex) { perPrayer[p].none++; possible++; } continue; }
      possible++;
      logged++;
      perPrayer[p][GROUP_OF[st]]++;
      if (PRAYED.includes(st)) prayed++;
      if (ON_TIME.includes(st)) onTime++;
      if (st === 'jamaah') jamaah++;
      if (st === 'missed') missed++;
    }
    for (const [k, n] of Object.entries(day?.sunnah ?? {})) if (n) { sunnah[k as SunnahKey].done++; sunnah[k as SunnahKey].rakats += n; }
    if (RAWATIB.every((k) => (day?.sunnah?.[k] ?? 0) > 0)) rawatibDays++;
  }
  return { days: days.length, possible, prayed, onTime, jamaah, missed, logged, perPrayer, sunnah, rawatibDays, excusedDays };
}

/** Fard prayed (any status but missed) that day, 0–5 — drives the heatmap. */
export const fardCount = (day: PrayerDay | undefined) => SALAH.filter((p) => { const s = day?.marks[p]; return !!s && PRAYED.includes(s); }).length;

export interface StreakDef { key: string; label: string; hit: (day: PrayerDay | undefined) => boolean }

export const STREAKS: StreakDef[] = [
  { key: 'all5', label: 'All 5 prayed', hit: (d) => fardCount(d) === 5 },
  { key: 'ontime', label: 'All 5 on time', hit: (d) => SALAH.every((p) => { const s = d?.marks[p]; return !!s && ON_TIME.includes(s); }) },
  { key: 'fajr', label: 'Fajr on time', hit: (d) => { const s = d?.marks.fajr; return !!s && ON_TIME.includes(s); } },
  { key: 'jamaah', label: 'All 5 in jamāʿah', hit: (d) => SALAH.every((p) => d?.marks[p] === 'jamaah') },
  { key: 'rawatib', label: '12 rawātib', hit: (d) => RAWATIB.every((k) => (d?.sunnah?.[k] ?? 0) > 0) },
  { key: 'witr', label: 'Witr', hit: (d) => (d?.sunnah?.witr ?? 0) > 0 },
  { key: 'duha', label: 'Ḍuḥā', hit: (d) => (d?.sunnah?.duha ?? 0) > 0 },
  { key: 'tahajjud', label: 'Tahajjud', hit: (d) => (d?.sunnah?.tahajjud ?? 0) > 0 },
];

/* Excused days (period) neither count towards nor break a streak. Today not
   being logged yet doesn't break it either. */
export function streakFor(log: Log, def: StreakDef, today: string, excused: (d: string) => boolean = () => false) {
  let current = 0;
  let d = today;
  if (!def.hit(log[d]) && !excused(d)) d = addDays(d, -1);
  for (let guard = 0; guard < 3650; guard++, d = addDays(d, -1)) {
    if (excused(d) && !def.hit(log[d])) continue;
    if (!def.hit(log[d])) break;
    current++;
  }
  const keys = Object.keys(log).filter((k) => k <= today).sort();
  let best = current, run = 0;
  if (keys.length) {
    for (let k = keys[0]; k <= today; k = addDays(k, 1)) {
      if (def.hit(log[k])) { run++; best = Math.max(best, run); }
      else if (!excused(k)) run = 0;
    }
  }
  return { current, best };
}
